import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import { useUserProfile } from '@/features/auth/api';
import { findBlockedWords, useBlockedWordsCheck } from '@/features/blocked-words/api';
import { useAuthStore } from '@/lib/auth/store';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarClock,
  Contact,
  Loader2,
  Megaphone,
  ScrollText,
  Send,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useAllocatedDedicatedClis,
  useBaseUploads,
  useComposeCampaign,
  useComposeCampaignV2,
  useComposeUpdateContinuousCampaign,
  useUpdateCampaign,
  useUploadBaseFile,
  useUploadContactList,
  useUserLocations,
  useUserModules,
} from '../api';
import { buildComposeRequest, buildOutdialV2Body, resolveTemplateId } from '../payload';
import {
  DEFAULT_FORM_VALUES,
  IS_CONTINUOUS,
  templateFeatures,
  validateContinuousSchedule,
  type TemplateId,
  type VoiceCampaignFormValues,
} from '../types';
import { StepBasics } from './steps/StepBasics';
import { StepContacts } from './steps/StepContacts';
import { StepContent } from './steps/StepContent';
import { StepNotifications } from './steps/StepNotifications';
import { StepReview } from './steps/StepReview';
import { StepRouting } from './steps/StepRouting';
import { StepSchedule } from './steps/StepSchedule';

type StepId =
  | 'basics'
  | 'schedule'
  | 'contacts'
  | 'content'
  | 'routing'
  | 'notifications'
  | 'launch';

interface ComposeWizardProps {
  mode: 'create' | 'update' | 'clone';
  initialValues?: Partial<VoiceCampaignFormValues>;
  /** Required for update; ignored in create/clone. */
  campaignId?: number;
  pageTitle: string;
  pageDescription: string;
  backHref?: string;
}

/**
 * Step list is template-driven:
 *  - Template 7 has no Content step (everything is picked in Basics).
 *  - Template 10 keeps all six steps — the flow pick in Content drives the
 *    inner 7/8/9 behaviour via `templateIdByTTS`.
 * IDs (not indices) drive validation/rendering because the list length
 * changes with the template.
 */
function buildSteps(
  templateId: TemplateId,
  templateIdByTTS: string,
  isContinuous: boolean,
): StepDescriptor[] {
  const steps: StepDescriptor[] = [{ id: 'basics', label: 'Basics', icon: Megaphone }];
  if (!isContinuous) {
    steps.push({ id: 'contacts', label: 'Contacts', icon: Contact });
  }
  if (isContinuous) {
    steps.push({ id: 'schedule', label: 'Schedule', icon: CalendarClock });
  }
  if (templateId !== '7' && templateIdByTTS !== '7') {
    steps.push({ id: 'content', label: 'Content', icon: ScrollText });
  }
  steps.push(
    { id: 'routing', label: 'Routing', icon: SlidersHorizontal },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'launch', label: 'Launch', icon: Send },
  );
  return steps;
}

/** Validate the current step. Return an error message or null. */
function validateStep(
  stepId: StepId,
  v: VoiceCampaignFormValues,
  baseFile: File | null,
  blockedWords: string[] = [],
  isContinuous: boolean,
): string | null {
  const f = templateFeatures(v.templateId, v.templateIdByTTS);

  if (stepId === 'basics') {
    if (!v.campaignName.trim()) return 'Give the campaign a name.';
    if (f.hasTts && !IS_CONTINUOUS(v.templateId)) {
      if (!v.ttsId) return 'Select TTS FlowId';
    }
    if (v.templateId === '10' && isContinuous) {
      if (!v.selectedTTSFlowId) return 'Select TTS FlowId';
    }
    return null;
  }

  if (stepId === 'schedule' && isContinuous) {
    if (f.needsSchedule || isContinuous) {
      const err = validateContinuousSchedule(v);
      if (err) return err;
    }
    if (v.useIvr) {
      if (!v.ivrId) return 'Select the IvrId';
    }
    return null;
  }

  if (stepId === 'contacts' && !isContinuous) {
    // Template 10 DOES provide a base — outdialV2 accepts the file/list
    // inline with the compose body, so the same validation applies.
    if (v.baseFileMode === 'contactList' && !v.contactList.trim())
      return 'Paste at least one phone number.';
    // TTS variable 30-char limit check on pasted data
    // TTS validation warning (numbers will be skipped server-side)
    if (v.baseFileMode === 'contactList' && v.contactList.trim()) {
      const lines = v.contactList
        .trim()
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const headerLine = lines[0] ?? '';
      const header = headerLine.split(',');
      const ttsIndices = header
        .map((h, i) => ({ h: h.trim().toLowerCase(), i }))
        .filter((x) => x.h.startsWith('tts_'))
        .map((x) => x.i);

      if (ttsIndices.length > 0) {
        let warnCount = 0;

        for (let r = 1; r < lines.length; r++) {
          const cols = lines[r]!.split(',');
          let bad = false;
          for (const ti of ttsIndices) {
            const val = (cols[ti] ?? '').trim();
            // Only count 30-char violations as skippable warnings
            // Blocked words are caught separately below and block the campaign
            if (val.length > 30) {
              bad = true;
              break;
            }
          }
          if (bad) warnCount++;
        }

        if (warnCount > 0 && warnCount >= lines.length - 1) {
          return `All ${warnCount} numbers have TTS validation issues. No valid numbers to upload.`;
        }
      }
    }
    // ── Blocked word check on pasted TTS variable content ──
    if (v.baseFileMode === 'contactList' && v.contactList.trim() && blockedWords.length > 0) {
      const bwLines = v.contactList
        .trim()
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      if (bwLines.length > 1) {
        const bwHeader = (bwLines[0] ?? '').toLowerCase().split(',');
        const bwTtsIndices = bwHeader
          .map((h, i) => (h.trim().startsWith('tts_') ? i : -1))
          .filter((i) => i > 0);
        if (bwTtsIndices.length > 0) {
          for (let r = 1; r < bwLines.length; r++) {
            const bwCols = (bwLines[r] ?? '').split(',');
            for (const ti of bwTtsIndices) {
              const val = bwCols[ti]?.trim() ?? '';
              if (val) {
                const matched = findBlockedWords(val, blockedWords);
                if (matched.length > 0) {
                  return `TTS variable "${bwHeader[ti]?.trim()}" contains blocked word "${matched[0]}" at line ${r + 1}. Remove it to proceed.`;
                }
              }
            }
          }
        }
      }
    }

    if (v.baseFileMode === 'newBaseFile' && !baseFile) return 'Pick a file to upload.';
    if (v.baseFileMode === 'newBaseFile' && !v.baseName.trim())
      return 'Give the uploaded list a name.';
    if (v.baseFileMode === 'oldBaseFile' && !v.oldBaseId) return 'Pick an existing contact list.';
    return null;
  }

  if (stepId === 'content') {
    // Required voice files (inner template for 10, direct for 0/1/2/3).
    if (f.requiredVoiceFiles.includes('welcome') && !v.welcomeId)
      return 'Pick a welcome voice file.';
    if (f.requiredVoiceFiles.includes('menu') && !v.menuId) return 'Pick a menu voice file.';
    if (f.requiredVoiceFiles.includes('noAgent') && !v.noAgentId)
      return 'Pick a "no agent available" voice file.';

    // Menu wait + re-prompt are required wherever the menu prompt is.
    if (f.needsMenuWait) {
      if (!v.menuWaitTime) return 'Menu wait time is required (1–10 seconds).';
      if (!v.rePrompt && v.rePrompt !== '0') return 'Re-prompt count is required (0–3).';
    }

    // DTMF picker — at least one valid key must be selected.
    if (f.hasDtmfPicker && v.dtmf.length === 0) return 'Select at least one DTMF digit.';

    // Patch list — at least one agent group must be added.
    if (f.hasPatchList && v.patchList.length === 0)
      return 'Add at least one agent group + DTMF row.';

    // Custom IVR / TTS Flow / TTS rows
    if (f.hasIvr && !v.ivrId) return 'Pick a Custom IVR flow.';

    return null;
  }

  if (stepId === 'routing') {
    if (v.locationIds.length === 0) return 'Select at least one location.';
    return null;
  }

  return null;
}

export function ComposeWizard({
  mode,
  initialValues,
  campaignId,
  pageTitle,
  pageDescription,
  backHref = '/voice-campaign-summary',
}: ComposeWizardProps) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const baseFileRef = useRef<HTMLInputElement>(null);
  const modules = useUserModules();

  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );
  const isContinuous = moduleNames.has('Continuous Campaign');
  // ── Number upload limit (from logged-in user's profile) ──
  const profile = useUserProfile();
  const numberUploadLimit = profile.data?.numberUploadLimit ?? null;
  const bases = useBaseUploads();
  const blockedWordsCheck = useBlockedWordsCheck();

  const [values, setValuesRaw] = useState<VoiceCampaignFormValues>(() => ({
    ...DEFAULT_FORM_VALUES,
    ...initialValues,
  }));

  const setValues = (patch: Partial<VoiceCampaignFormValues>) => {
    // Changing file mode or file name invalidates the previously uploaded
    // baseId (legacy path) and the stashed file (outdialV2 path).
    if ('baseFileMode' in patch || 'baseName' in patch) {
      setUploadedBaseId(null);
      setPendingBaseFile(null);
    }
    setValuesRaw((prev) => ({ ...prev, ...patch }));
  };

  // ── Template-driven step list (Content omitted for template 7) ──
  const steps = useMemo(
    () => buildSteps(values.templateId, values.templateIdByTTS, isContinuous),
    [values.templateId, values.templateIdByTTS, isContinuous],
  );
  const stepper = useStepper(steps.length);

  // Safety net: if the step list shrank while parked on a later index,
  // pull the index back into range.
  useEffect(() => {
    if (stepper.current > steps.length - 1) {
      stepper.goTo(steps.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);

  const currentIndex = Math.min(stepper.current, steps.length - 1);
  const currentId = (steps[currentIndex]?.id ?? 'basics') as StepId;
  const isLastStep = currentIndex === steps.length - 1;

  // Legacy path: baseId returned by the eager file upload on Contacts → Next.
  const [uploadedBaseId, setUploadedBaseId] = useState<number | null>(null);
  // outdialV2 path: the picked file is stashed on Contacts and sent inline
  // with the compose body — never pre-uploaded, no separate baseId.
  const [pendingBaseFile, setPendingBaseFile] = useState<File | null>(null);

  const uploadContacts = useUploadContactList();
  const uploadFile = useUploadBaseFile();
  const compose = useComposeCampaign();
  const composeV2 = useComposeCampaignV2();
  const updateContinouosCampaign = useComposeUpdateContinuousCampaign();
  const update = useUpdateCampaign();

  // Denormalize locationName + cli value into the compose payload —
  // backend expects objects (`{locationId, locationName}`, `{cliId, cli}`)
  // not bare ids, otherwise it throws "JSONArray[0] is not a JSONObject".
  const userLocations = useUserLocations();
  const userClis = useAllocatedDedicatedClis();
  const locationNameById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const loc of userLocations.data ?? []) {
      out[String(loc.locationId)] = loc.locationName;
    }
    return out;
  }, [userLocations.data]);
  const cliNameById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of userClis.data ?? []) {
      out[String(c.cliId)] = c.cli;
    }
    return out;
  }, [userClis.data]);

  const isPending =
    uploadContacts.isPending ||
    uploadFile.isPending ||
    compose.isPending ||
    composeV2.isPending ||
    update.isPending ||
    updateContinouosCampaign.isPending;

  const handleNext = async () => {
    const file = baseFileRef.current?.files?.[0] ?? null;
    const err = validateStep(
      currentId,
      values,
      file,
      blockedWordsCheck.data?.words ?? [],
      isContinuous,
    );
    if (err) {
      toast.error(err);
      return;
    }

    // ── Number upload limit check on Contacts step ──
    if (currentId === 'contacts' && numberUploadLimit !== null && numberUploadLimit !== undefined) {
      let count = 0;

      if (values.baseFileMode === 'contactList' && values.contactList.trim()) {
        const lines = values.contactList
          .trim()
          .split('\n')
          .filter((l) => l.trim());
        const hasHeader = lines.length > 0 && /^[a-z_]/i.test(lines[0]!) && lines[0]!.includes(',');
        count = hasHeader ? lines.length - 1 : lines.length;
      } else if (values.baseFileMode === 'newBaseFile' && file) {
        const text = await file.text();
        const lines = text
          .trim()
          .split('\n')
          .filter((l) => l.trim());
        const hasHeader = lines.length > 0 && /^[a-z_]/i.test(lines[0]!) && lines[0]!.includes(',');
        count = hasHeader ? lines.length - 1 : lines.length;
      } else if (values.baseFileMode === 'oldBaseFile' && values.oldBaseId) {
        const selected = (bases.data ?? []).find((b) => String(b.baseId) === values.oldBaseId);
        count = selected?.totalNumbers ?? 0;
      }

      if (count > 0 && count > numberUploadLimit) {
        if (numberUploadLimit === 0) {
          toast.error(
            'Your number upload limit is exhausted. Please contact your manager to get more limit.',
          );
        } else {
          toast.error(
            `You can upload only ${numberUploadLimit.toLocaleString()} more numbers (your current limit). To increase your limit, please contact your manager/parent.`,
          );
        }
        return;
      }
    }

    if (currentId === 'contacts') {
      if (isContinuous) {
        // outdialV2 accepts the file inside the compose body — just stash it.
        setPendingBaseFile(values.baseFileMode === 'newBaseFile' ? file : null);
      } else if (values.baseFileMode === 'newBaseFile' && file) {
        // Legacy path: eager upload so handleSubmit (last step) has a baseId.
        try {
          const templateId = resolveTemplateId(values);
          const res = await uploadFile.mutateAsync({
            userId: Number(userId),
            templateId,
            baseFile: file,
            baseName: values.baseName.trim(),
            contactList: '',
          });
          if (res.baseId == null) {
            toast.error(res.message ?? 'File upload failed.');
            return;
          }
          setUploadedBaseId(Number(res.baseId));
          if (res.skippedCount && res.skippedCount > 0) {
            toast.warning(
              `${res.skippedCount} number${res.skippedCount > 1 ? 's' : ''} skipped (TTS variable exceeds 30 characters).`,
            );
          }
        } catch {
          toast.error('Could not upload the file.');
          return;
        }
      }

      if (values.baseFileMode !== 'newBaseFile') {
        setUploadedBaseId(null);
        setPendingBaseFile(null);
      }
    }

    // goTo instead of next() — safe when the step list length differs from
    // what the hook was first initialized with (template 7 drops a step).
    stepper.goTo(currentIndex + 1);
  };

  const resolveBaseId = async (): Promise<number | null> => {
    if (isContinuous) {
      // outdialV2 carries the file / pasted list in the request body, so a
      // baseId is only meaningful for an existing base. 0 = "inline".
      if (values.baseFileMode === 'oldBaseFile') {
        const id = Number(values.oldBaseId);
        return Number.isFinite(id) ? id : 0;
      }
      return 0;
    }
    const templateId = resolveTemplateId(values);
    if (values.baseFileMode === 'oldBaseFile') {
      const id = Number(values.oldBaseId);
      return Number.isFinite(id) ? id : null;
    }
    if (values.baseFileMode === 'contactList') {
      const res = await uploadContacts.mutateAsync({
        userId: Number(userId),
        templateId,
        contactList: values.contactList,
      });
      if (res.baseId == null) {
        toast.error(res.message ?? 'Contact list upload failed.');
        return null;
      }
      if (res.skippedCount && res.skippedCount > 0) {
        toast.warning(
          `${res.skippedCount} number${res.skippedCount > 1 ? 's' : ''} skipped (TTS variable exceeds 30 chars).`,
        );
      }
      return res.baseId;
    }
    // File was already uploaded eagerly in handleNext (Contacts → Next).
    if (uploadedBaseId != null) return uploadedBaseId;
    // Fallback: if ref is still alive (shouldn't happen in normal flow).
    const file = baseFileRef.current?.files?.[0];
    if (!file) return null;
    const res = await uploadFile.mutateAsync({
      userId: Number(userId),
      templateId,
      baseFile: file,
      baseName: values.baseName.trim(),
      contactList: '',
    });
    return res.baseId ?? null;
  };

  const handleSubmit = async () => {
    if (isContinuous) {
      // Users can jump to the last step via the stepper, so re-validate here.
      if (!values.startDate || !values.endDate) {
        toast.error('Complete the campaign schedule before launching.');
        return;
      }
      if (values.endDate < values.startDate) {
        toast.error('End date cannot be before the start date.');
        return;
      }
      if (values.baseFileMode === 'newBaseFile') {
        const f = pendingBaseFile ?? baseFileRef.current?.files?.[0] ?? null;
        if (!f) {
          toast.error('Pick a file to upload.');
          return;
        }
      }
    } else if (values.sendLater) {
      if (!values.scheduleDate) {
        toast.error('Pick a date to schedule the campaign.');
        return;
      }
      if (new Date(values.scheduleDate) < new Date()) {
        toast.error('Scheduled time must be in the future.');
        return;
      }
    }

    try {
      const baseId = await resolveBaseId();
      if (baseId === null) {
        toast.error('Could not resolve a contact list.');
        return;
      }
      const req = buildComposeRequest({
        userId: Number(userId),
        values,
        baseId,
        locationNameById,
        cliNameById,
      });
      // Ring Duration module disabled → don't send the default 10 (backend would reject it)
      if (!moduleNames.has('Ring Duration')) {
        req.ringDuration = undefined;
      }

      let res: { message?: string; status?: string };
      if (isContinuous) {
        // Template 10 → single multipart call: full compose body + base file/list.
        const body = buildOutdialV2Body({
          req,
          values,
          baseFile:
            values.baseFileMode === 'newBaseFile'
              ? (pendingBaseFile ?? baseFileRef.current?.files?.[0] ?? null)
              : null,
          campaignId: mode === 'update' ? campaignId : undefined,
        });

        res =
          mode === 'update' && campaignId
            ? await updateContinouosCampaign.mutateAsync(body)
            : await composeV2.mutateAsync(body);
      } else {
        res =
          mode === 'update' && campaignId
            ? await update.mutateAsync({ ...req, campaignId })
            : await compose.mutateAsync(req);
      }

      if (res?.message === 'Parameters Incorrect' || res.status === 'fail') {
        toast.error(res.message);
        return;
      }
      toast.success(
        res?.message ?? (mode === 'update' ? 'Campaign updated' : 'Campaign submitted'),
      );
      navigate(backHref);
    } catch {
      toast.error('Could not submit the campaign.');
    }
  };
  // Switch on step id — indices shift when the Content step is omitted.
  const stepBody = useMemo(() => {
    switch (currentId) {
      case 'basics':
        return (
          <StepBasics values={values} setValues={setValues} lockTemplate={mode === 'update'} />
        );
      case 'contacts':
        return <StepContacts values={values} setValues={setValues} baseFileRef={baseFileRef} />;
      case 'schedule':
        return <StepSchedule values={values} setValues={setValues} />;
      case 'content':
        return <StepContent values={values} setValues={setValues} />;
      case 'routing':
        return <StepRouting values={values} setValues={setValues} />;
      case 'notifications':
        return <StepNotifications values={values} setValues={setValues} />;
      case 'launch':
        return <StepReview values={values} setValues={setValues} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, values, mode]);

  return (
    <ScrollPage>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          <Button asChild variant="outline">
            <Link to={backHref}>
              <ArrowLeft className="h-4 w-4" />
              Back to campaigns
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={steps}
        current={currentIndex}
        completed={stepper.completed}
        onStepClick={stepper.goTo}
      />

      <div className="mt-6 space-y-4">{stepBody}</div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={() => stepper.goTo(currentIndex - 1)}
          disabled={currentIndex === 0 || isPending}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {isLastStep ? (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {mode === 'update' ? 'Save changes' : 'Submit campaign'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={isPending}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ScrollPage>
  );
}
